/**
 * 手机号绑定用例
 */

import { computed, onUnmounted, reactive, ref } from 'vue'
import { getCSDClient } from '@/lib/csd-adapter'
import { refreshBindUserInfo } from './useBindAccountState'

export function useBindPhone() {
  const form = reactive({
    phone: '',
    code: ''
  })

  const captcha = reactive({
    key: '',
    image: '',
    code: ''
  })

  const showCaptcha = ref(false)
  const sendingCode = ref(false)
  const submitting = ref(false)
  const countdown = ref(0)
  let countdownTimer: ReturnType<typeof setInterval> | null = null

  const canSendCode = computed(() => {
    const phoneValid = /^1[3-9]\d{9}$/.test(form.phone)
    if (!showCaptcha.value) {
      return phoneValid && !sendingCode.value && countdown.value <= 0
    }

    return phoneValid && captcha.code.length === 4 && !sendingCode.value && countdown.value <= 0
  })

  function getClientOrThrow() {
    const client = getCSDClient()
    if (!client) {
      throw new Error('CSD SDK 未初始化')
    }
    return client
  }

  function reset() {
    form.phone = ''
    form.code = ''
    captcha.key = ''
    captcha.image = ''
    captcha.code = ''
    showCaptcha.value = false
    sendingCode.value = false
    submitting.value = false
    countdown.value = 0

    if (countdownTimer) {
      clearInterval(countdownTimer)
      countdownTimer = null
    }
  }

  async function loadCaptcha() {
    const client = getClientOrThrow()
    const result = await client.getAuthService().getImageCaptcha()
    captcha.key = result.captcha_key
    captcha.image = result.captcha_image_content.startsWith('data:')
      ? result.captcha_image_content
      : `data:image/png;base64,${result.captcha_image_content}`
    captcha.code = ''
  }

  async function sendSms() {
    if (!/^1[3-9]\d{9}$/.test(form.phone)) {
      throw new Error('请输入正确的手机号')
    }

    if (!showCaptcha.value) {
      showCaptcha.value = true
      await loadCaptcha()
      return { requireCaptcha: true }
    }

    if (captcha.code.length !== 4) {
      throw new Error('请输入4位图形验证码')
    }

    const client = getClientOrThrow()
    sendingCode.value = true

    try {
      await client.getAuthService().sendVerifyCodeWithCaptcha({
        phone: form.phone,
        captcha_key: captcha.key,
        captcha_code: captcha.code,
        smsType: 'bind'
      })

      showCaptcha.value = false
      captcha.key = ''
      captcha.image = ''
      captcha.code = ''

      countdown.value = 60
      countdownTimer = setInterval(() => {
        countdown.value -= 1
        if (countdown.value <= 0 && countdownTimer) {
          clearInterval(countdownTimer)
          countdownTimer = null
        }
      }, 1000)

      return { requireCaptcha: false }
    } catch (error) {
      if (showCaptcha.value) {
        await loadCaptcha()
      }
      throw error
    } finally {
      sendingCode.value = false
    }
  }

  async function submit() {
    if (!/^1[3-9]\d{9}$/.test(form.phone)) {
      throw new Error('请输入正确的手机号')
    }

    if (!/^\d{4,6}$/.test(form.code)) {
      throw new Error('请输入正确的短信验证码')
    }

    const client = getClientOrThrow()
    submitting.value = true
    try {
      await client.getUserService().bindPhone({
        phone: form.phone,
        code: form.code
      })
      await refreshBindUserInfo()
    } finally {
      submitting.value = false
    }
  }

  onUnmounted(() => {
    if (countdownTimer) {
      clearInterval(countdownTimer)
    }
  })

  return {
    form,
    captcha,
    showCaptcha,
    sendingCode,
    submitting,
    countdown,
    canSendCode,
    loadCaptcha,
    sendSms,
    submit,
    reset
  }
}
